import { BO, BusinessObjectBase, CanExecute, CollectionChangedJoinPoint, FeatureFlagsRulesServiceBase, PropertyChangedJoinPoint, QueryBus, Weaver } from '@skylab-shared/core';
import { AddressModel, CalculationMethod, Company, DiscountKind, DocumentClientModel, DocumentPaymentListItemModel, ExchangeRateModel, InventoryEffect, InventoryGoodsIssueAdjustmentDocumentModel, InventoryGoodsIssueDocumentModel, JpkVatCodes, OrderModel, RelatedEntity, SaleDocumentAdjustmentModel, SaleDocumentItemModel, SaleDocumentKind, SaleDocumentModel, SaleProFormaModel, SplitPaymentState } from '@skylab-shared/dataModel';
import { dateDiffInDays, getMaxOfDates, getMinimalOfDates, substractDays, unique } from '@skylab-shared/utils';
import { ClientRepositoryBase } from '../client/client.repo-base';
import { ContextKeys } from '../common/context-keys';
import { getCorrectInventoryEffectFromSourceDocument } from '../common/document-utils';
import { getJpkVatGroups } from '../common/get-jpk-groups';
import { propertyChangedJoinPointFilter } from '../common/property-changed/property-changed-filter';
import { NipValidator } from '../common/validators/nip-validator';
import { CommunityTransactionForSaleQuery } from '../community-transaction/queries/community-transaction-for-sale.query-base';
import { CompanyParametersQuery } from '../company/queries/company-parameters.query-base';
import { CountryCollectionQuery } from '../country/queries/country.query-base';
import { CurrencyCollectionQuery } from '../currency/queries/currency.query-base';
import { DocumentExchangeRateValidationBoPart } from '../documents/bo-parts/document-exchange-rate-validation.bo-part';
import { DocumentItemsNumberingBoPart } from '../documents/bo-parts/document-items-numbering.bo-part';
import { DocumentNumberBoPart } from '../documents/bo-parts/document-number.bo-part';
import { DocumentVatRateForSaleValidationBoPart } from '../documents/bo-parts/document-vat-rate-for-sale-validation.bo-part';
import { DocumentWstoBoPart } from '../documents/bo-parts/document-wsto.bo-part';
import { CommunityTransactionForSaleMixin } from '../documents/mixins/community-transaction-for-sale.mixin';
import { ExchangeRateMixin } from '../documents/mixins/exchange-rate.mixin';
import { ToPayMixin } from '../documents/mixins/to-pay.mixin';
import { DocumentLink } from '../documents/queries/document-links.query-base';
import { fillDocumentClientFromOrderClient, rewriteAddress, rewriteDocumentContractor, rewriteDocumentContractorWithFreshDataAsync } from '../documents/utils/document-client-utils';
import { rewriteExchangeRate } from '../documents/utils/document-exchange-rate-utils';
import { rewriteDocumentItem } from '../documents/utils/document-item-utils';
import { NumberingQuery } from '../numbering/queries/numbering.query-base';
import { PaymentMethodQuery } from '../payment-method/queries/payment-method.query-base';
import { ProductGetIdsByExternalIdsQuery, ProductGetIdsByExternalIdsResult } from '../product/queries/product-get-ids-by-external-ids.query-base';
import { ProductRepositoryContract } from '../shared-business-temporary-contracts';
import { UnitQuery } from '../unit/queries/unit.query-base';
import { LoggedUserQuery } from '../user/queries/logged-user.query-base';
import { UserCollectionQuery } from '../user/queries/user.query-base';
import { BusinessDayCalculatorPL } from '../utils/business-days-calculator';
import { collectionChangedJoinPointFilter } from '../utils/collection-changed-filter';
import { VatRateForSaleQuery } from '../vat/queries/vat-rate-for-sale.query-base';
import { VatReleaseReasonQuery } from '../vat/queries/vat-release-reason.query-base';
import { BankAccountNumberMixin } from './mixins/bank-account.mixin';
import { ClientMixin } from './mixins/client.mixin';
import { SaleLinksMixin } from './mixins/sale-links.mixin';
import { TaxLiabilityDateMixin } from './mixins/tax-liability-date.mixin';
import { SaleDocumentCalculatorBoPart } from './parts/sale-document-calculator.bo-part';
import { SaleDocumentClientBoPart } from './parts/sale-document-client.bo-part';
import { SaleDocumentIssuedToInventoryGoodsIssueBoPart } from './parts/sale-document-issued-to-inventory-goods-issue.bo-part';
import { SaleDocumentItemsValidationsBoPart } from './parts/sale-document-items-validations.bo-part';
import { SaleDocumentJpkGroupsBoPart } from './parts/sale-document-jpk-groups.bo-part';
import { SaleDocumentKsefBoPart } from './parts/sale-document-ksef.bo-part';
import { SaleDocumentPaymentsBoPart } from './parts/sale-document-payment.bo-part';
import { SaleDocumentReverseChargeAndNPBoPart } from './parts/sale-document-reverse-charge-and-NP.bo-part';
import { SaleDocumentSplitPaymentBoPart } from './parts/sale-document-split-payment.bo-part';
import { SaleDocumentVatReleaseBoPart } from './parts/sale-document-vat-release.bo-part';
import { SaleDocumentWithDueDateBoPart } from './parts/sale-document-with-due-date.bo-part';
import { SaleDocumentPlainObjectQuery } from './queries/sale-document-plain-object.query-base';
import { SaleKindQuery } from './queries/sale-kind.query-base';
import { getCalculationMethodsForSaleDocumentKind } from './utils/sale-document-calculators-utils';
import { rewritePaymentMethodFromOrderDocument } from './utils/sale-payment-rewriting-utils';
import { rewritePlainObjectToDocumentModel, rewriteSaleDocumentFromProFormaAsync, rewriteSaleDocumentItem, rewriteVatReleaseProperties } from './utils/sale-rewriting-utils';
import { CommunityTransactionsForReceipt, getDefaultNPValues, refreshSaleDocumentItemPriceAsync } from './utils/sale-utils';
import { DateIncludedInAccountingWarning } from './violations/date-included-in-accounting-warning';
import { IssueDateEarlierThanIssuedReceiptError } from './violations/issue-date-earlier-than-issued-receipt-error';
import { ItemQuantityHigherThanReceiptItemQuantityError } from './violations/item-quantity-higher-than-receipt-item-quantity-error';
import { SameTinOnInvoiceToReceiptWarning } from './violations/same-tin-on-invoice-to-receipt-warning';
import { WrongCalculationMethodUsedOnDocumentError } from './violations/wrong-calculation-method-used-on-document-error';

export interface SaleDocumentBO extends ClientMixin<SaleDocumentModel>, ExchangeRateMixin<SaleDocumentModel>, CommunityTransactionForSaleMixin<SaleDocumentModel>, ToPayMixin<SaleDocumentModel>, SaleLinksMixin<SaleDocumentModel>, TaxLiabilityDateMixin<SaleDocumentModel>, BankAccountNumberMixin<SaleDocumentModel> {};

@BO({
    modelType: SaleDocumentModel,
    mixins: [ExchangeRateMixin, CommunityTransactionForSaleMixin, ToPayMixin, SaleLinksMixin, TaxLiabilityDateMixin, BankAccountNumberMixin, ClientMixin]
})
export class SaleDocumentBO extends BusinessObjectBase<SaleDocumentModel> {
    public constructor(
        weaver: Weaver,
        protected readonly productRepository: ProductRepositoryContract,
        protected readonly clientRepository: ClientRepositoryBase,
        protected readonly featureFlagsRulesService: FeatureFlagsRulesServiceBase,
        protected readonly queryBus: QueryBus
    ) {
        super(weaver);
        this.initBOParts();
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.resolve([
            this.localContext.preload(CurrencyCollectionQuery),
            this.localContext.preload(PaymentMethodQuery),
            this.localContext.preload(SaleKindQuery),
            this.localContext.preload(VatRateForSaleQuery),
            this.localContext.preload(CommunityTransactionForSaleQuery),
            this.localContext.preload(LoggedUserQuery),
            this.localContext.preload(UserCollectionQuery),
            this.localContext.preload(CompanyParametersQuery),
            this.localContext.preload(NumberingQuery),
            this.localContext.preload(VatReleaseReasonQuery),
            this.localContext.preload(ContextKeys.company),
            this.localContext.preload(ContextKeys.isInvoiceToReceipt(this.model.sun), () => Promise.resolve(this.invoiceIssuedToReceipt)),
            this.localContext.preload(ContextKeys.isInvoiceToInventoryGoodsIssue(this.model.sun), () => Promise.resolve(this.invoiceIssuedToInventoryGoodsIssue)),
            this.localContext.preload(ContextKeys.relatedGoodsIssueDocumentToInvoice(this.model.sun), () => Promise.resolve(this.internalRelatedGoodsIssueDocument))
        ]);


        if (this.model.isPersistent) {
            await this.loadSourceClient();
            await this.loadLinksAsync();
            await this.configureLinksAsync();
        }

        this.registerReadonlyRules();
        this.registerReadonlyOmitRules();
        this.registerValidationRules();
        this.registerPropretyChanged();
    }

    private initBOParts(): void {
        this.parts.push(...[
            new SaleDocumentItemsValidationsBoPart(this.weaver, this.productRepository, this.featureFlagsRulesService, this.queryBus),
            new DocumentVatRateForSaleValidationBoPart(this.weaver),
            new DocumentItemsNumberingBoPart(this.weaver),
            new SaleDocumentCalculatorBoPart(this.weaver),
            new SaleDocumentWithDueDateBoPart(this.weaver),
            new SaleDocumentPaymentsBoPart(this.weaver),
            new SaleDocumentClientBoPart(this.weaver, this.featureFlagsRulesService),
            new SaleDocumentSplitPaymentBoPart(this.weaver, this.productRepository, this.featureFlagsRulesService),
            new SaleDocumentReverseChargeAndNPBoPart(this.weaver),
            new DocumentNumberBoPart(this.weaver, this.queryBus),
            new DocumentWstoBoPart(this.weaver, this.queryBus),
            new SaleDocumentJpkGroupsBoPart(this.weaver, this.featureFlagsRulesService),
            new SaleDocumentVatReleaseBoPart(this.weaver, this.queryBus),
            new DocumentExchangeRateValidationBoPart(this.weaver),
            new SaleDocumentKsefBoPart(this.weaver),
            new SaleDocumentIssuedToInventoryGoodsIssueBoPart(this.weaver)
        ]);
    }

    private registerReadonlyOmitRules(): void {
        this.readonlyChecker.addOmitRule([
            [SaleDocumentModel, 'dueDate'],
            [SaleDocumentModel, 'dueDateInDays'],
            [SaleDocumentModel, 'paymentMethodId']
        ], () => this.invoiceIssuedToReceipt && !this.linksAdjusted);

        this.readonlyChecker.addOmitRule([
            [SaleDocumentItemModel, 'value_netBeforeDiscount'],
            [SaleDocumentItemModel, 'value_grossBeforeDiscount'],
            [SaleDocumentItemModel, 'value_netAfterDiscount'],
            [SaleDocumentItemModel, 'value_grossAfterDiscount'],
        ], model => !this.isPropertyReadonly(model, 'quantity'));
    }

    private registerReadonlyRules(): void {
        this.readonly.addRule(
            [[SaleDocumentModel, 'kind']],
            () => this.model.isPersistent
        );

        this.readonly.addRule(
            [[SaleDocumentModel, 'kind']],
            [[SaleDocumentModel, 'communityTransactionId']],
            ({ source: { kind } }) => kind === SaleDocumentKind.VatMarginInvoice
        );

        this.readonly.addRule(
            [[SaleDocumentModel, 'kind']],
            [[SaleDocumentModel, 'calculationMethod']],
            ({ source: { kind } }) => kind === SaleDocumentKind.Receipt || kind === SaleDocumentKind.VatMarginInvoice
        );

        //#region Invoice issued to receipt

        this.readonly.addRule([
            [SaleDocumentModel, 'kind'],
            [SaleDocumentModel, 'calculationMethod'],
            [SaleDocumentModel, 'bankAccountNumberId'],
            [SaleDocumentModel, 'baseCurrencyBankAccountNumberId'],
            [SaleDocumentModel, 'saleDate'],
            [SaleDocumentModel, 'goodsIssueDate'],
            [SaleDocumentModel, 'currencyId'],
            [SaleDocumentModel, 'communityTransactionId'],
            [SaleDocumentModel, 'issuedByVatPayer'],
            [SaleDocumentModel, 'rateNP'],
            [SaleDocumentModel, 'reverseCharge'],
            [SaleDocumentModel, 'paymentMethodId'],
            [SaleDocumentModel, 'dueDate'],
            [SaleDocumentModel, 'dueDateInDays']],
            () => this.invoiceIssuedToReceipt
        );

        this.readonly.setReadonlyOnSubtree(SaleDocumentItemModel, () => this.invoiceIssuedToReceipt, {
            exceptions: [
                [SaleDocumentItemModel, <keyof SaleDocumentItemModel>'note'],
                [SaleDocumentItemModel, <keyof SaleDocumentItemModel>'quantity']
            ]
        });

        //#endregion

        //#region Adjusted document || Receipt With Issued Invoice

        this.readonly.addRule([
            [SaleDocumentModel, 'communityTransactionId'],
            [SaleDocumentModel, 'currencyId'],
            [SaleDocumentModel, 'wstoConsumerCountryId'],
            [SaleDocumentModel, 'wstoShipmentCountryId'],
            [SaleDocumentModel, 'jpkVatGroups'],
            [SaleDocumentModel, 'calculationMethod'],
            [SaleDocumentModel, 'transactionLocation'],
            [SaleDocumentModel, 'issueDate'],
            [SaleDocumentModel, 'saleDate'],
            [SaleDocumentModel, 'goodsIssueDate'],
            [SaleDocumentModel, 'dueDate'],
            [SaleDocumentModel, 'dueDateInDays'],
            [SaleDocumentModel, 'paymentMethodId'],
            [SaleDocumentModel, 'bankAccountNumberId'],
            [SaleDocumentModel, 'baseCurrencyBankAccountNumberId'],
            [SaleDocumentModel, 'issuer'],
            [SaleDocumentModel, 'recipient'],
            [SaleDocumentModel, 'client'],
            [SaleDocumentModel, 'buyer'],
            [SaleDocumentModel, 'marginSchemeId'],
            [SaleDocumentModel, 'vatReleaseReasonId'],
            [SaleDocumentModel, 'vatReleaseReasonCustomName'],
            [SaleDocumentModel, 'vatReleaseReasonSource'],
            [SaleDocumentModel, 'customDueDateDescription'],
            [SaleDocumentModel, 'numberingId'],
            [SaleDocumentModel, 'issuedByVatPayer'],
            [SaleDocumentModel, 'ksefSubmissionMode'],
            [SaleDocumentModel, 'orderNumber'],
            [SaleDocumentModel, 'orderDate'],
            [SaleDocumentModel, 'agreementNumber'],
            [SaleDocumentModel, 'agreementDate'],
            [SaleDocumentModel, 'rateNP'],
            [SaleDocumentModel, 'reverseCharge'],
        ], () => this.linksAdjusted || this.linksReceiptWithIssuedInvoice);

        this.readonly.setReadonlyOnSubtree(SaleDocumentItemModel, () => this.linksAdjusted || this.linksReceiptWithIssuedInvoice, {
            exceptions: [
                [SaleDocumentItemModel, <keyof SaleDocumentItemModel>'note']
            ]
        });

        this.readonly.addRule([
            [SaleDocumentModel, 'exchangeRate'],
            [SaleDocumentModel, 'splitPayment']
        ], () => this.linksAdjusted);

        this.readonly.setReadonlyOnSubtree(ExchangeRateModel, () => this.linksAdjusted);
        this.readonly.setReadonlyOnSubtree(DocumentClientModel, () => this.linksAdjusted || this.linksReceiptWithIssuedInvoice);
        this.readonly.setReadonlyOnSubtree(AddressModel, () => this.linksAdjusted || this.linksReceiptWithIssuedInvoice);

        //#endregion
    }

    private registerValidationRules(): void {
        this.registerCalculationMethodValidationRules();
        this.registerProFormaRelationRules();
        this.registerTaxLiabilityDateValidationRules();
        this.registerIssueDateEarlierThanIssuedReceiptDate();
        this.registerSameTinOnInvoiceToReceipt();
        this.registerOrderDocumentValidation();
    }

    private registerOrderDocumentValidation(): void {
        this.readonly.addRule([
            [SaleDocumentModel, 'documentNumber'],
            [SaleDocumentModel, 'numberingId']
        ], () => this.model.isPersistent && !!this.model.orderExternalId);
    }

    private registerSameTinOnInvoiceToReceipt(): void {
        this.validation.addRule(
            [[SaleDocumentModel, 'client']],
            ctx => {
                if (this.invoiceIssuedToReceipt && ctx.source.client) {
                    return (ctx.source.client.tinKind === this.sourceReceiptModel.client?.tinKind)
                        && (NipValidator.normalize(ctx.source.client.tin) === NipValidator.normalize(this.sourceReceiptModel.client?.tin));
                }
                return true;
            },
            SameTinOnInvoiceToReceiptWarning
        );
    }

    private registerIssueDateEarlierThanIssuedReceiptDate(): void {
        this.validation.addRule(
            [[SaleDocumentModel, 'issueDate']],
            ctx => this.invoiceIssuedToReceipt
                ? dateDiffInDays(ctx.source.issueDate, this.sourceReceiptModel.issueDate) >= 0
                : true,
            IssueDateEarlierThanIssuedReceiptError
        );
    }

    private registerCalculationMethodValidationRules(): void {
        this.validation.addRule(
            [[SaleDocumentModel, 'kind'], [SaleDocumentModel, 'calculationMethod'], [SaleDocumentModel, 'issuedByVatPayer']],
            [[SaleDocumentModel, 'calculationMethod']],
            ctx => {
                if (!ctx.source.kind) return true;
                if (!ctx.source.issuedByVatPayer && ctx.source.calculationMethod !== CalculationMethod.Gross) return false;
                const availableCalculationMethods = getCalculationMethodsForSaleDocumentKind(ctx.source.kind);
                return availableCalculationMethods.indexOf(ctx.source.calculationMethod) !== -1;
            },
            WrongCalculationMethodUsedOnDocumentError
        );
    }

    private registerProFormaRelationRules(): void {
        this.validation.addRule(
            [[SaleDocumentItemModel, 'quantity']],
            ctx => {
                if (this.invoiceIssuedToReceipt) {
                    const [refferingItemModel] = this.sourceReceiptModel.items.filter(i => i.ordinalNumber === ctx.source.ordinalNumber);
                    if (refferingItemModel) {
                        return ctx.source.quantity <= refferingItemModel.quantity;
                    }
                }
                return true;
            },
            ItemQuantityHigherThanReceiptItemQuantityError
        );
    }

    private registerTaxLiabilityDateValidationRules(): void {
        this.validation.addRule(
            [[SaleDocumentModel, 'taxLiabilityDate']],
            async ctx => ctx.source.issuedByVatPayer ? !(await this.isTaxLiabilityDateIncludedInAccountancyAsync()) : true,
            DateIncludedInAccountingWarning
        );
    }

    private registerPropretyChanged(): void {
        this.applyDiscountToNewItemOnCollectionChanged();
        this.registerCostRuleAfterRateChange();
    }

    private applyDiscountToNewItemOnCollectionChanged(): void {
        const reactToDocumentItemsCollectionChange = collectionChangedJoinPointFilter(this)(this.model.items);
        this.registerListening(this.weaver.register(CollectionChangedJoinPoint, reactToDocumentItemsCollectionChange(
            ctx => (ctx.newItems as any[]).forEach((item: SaleDocumentItemModel) => this.applyClientDiscountToItem(item))
        )));
    }

    private registerCostRuleAfterRateChange() {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(ExchangeRateModel, ['rate'])(
            () => this.validation.queueValidation([SaleDocumentItemModel, 'price_grossBeforeDiscount'])
        )));
    }

    protected calculateDateForCurrencyExchangeRate(): Date {
        const date = this.model.issuedByVatPayer
            ? getMinimalOfDates(this.model.issueDate, this.model.taxLiabilityDate)
            : dateDiffInDays(this.model.issueDate, this.model.saleDate) < 0 ? this.model.issueDate : this.model.saleDate;

        return BusinessDayCalculatorPL.getLastBusinessDay(substractDays(date!, 1));
    }

    private async configureLinksAsync(): Promise<void> {
        // faktura do paragonu
        const invoiceIssuedToReceipt = this.linksInvoiceIssuedToReceipt;
        const invoiceIssuedToInventoryGoodsIssue = this.linksInvoiceIssuedToInventoryGoodsIssue;
        if (invoiceIssuedToReceipt) {
            const sourceLink = this.linksInvoiceIssuedToReceiptSourceLink;
            await this.setSourceReceiptModelAsync(sourceLink?.documentId);
            await this.setInvoiceIssuedToReceiptAsync(invoiceIssuedToReceipt);
        }

        if (invoiceIssuedToInventoryGoodsIssue) {
            await this.setInvoiceIssuedToInventoryGoodsIssueAsync(invoiceIssuedToInventoryGoodsIssue);
        }
    }

    private sourceReceiptModel!: SaleDocumentModel | SaleDocumentAdjustmentModel;
    private async setSourceReceiptModelAsync(id: number | undefined): Promise<void> {
        const plainObject = await this.queryBus.queryAsync(new SaleDocumentPlainObjectQuery(), id);
        this.sourceReceiptModel = rewritePlainObjectToDocumentModel(plainObject);
    }

    private internalInvoiceIssuedToReceipt = false;
    private async setInvoiceIssuedToReceiptAsync(value: boolean) {
        this.internalInvoiceIssuedToReceipt = value;
        await this.localContext.refresh(ContextKeys.isInvoiceToReceipt(this.model.sun));
    }

    public get invoiceIssuedToReceipt(): boolean {
        return this.internalInvoiceIssuedToReceipt;
    }

    @CanExecute()
    public async fillInvoiceIssuedToReceiptAsync(sourceId: number, sourceDocumentLink?: DocumentLink): Promise<void> {
        await this.setSourceReceiptModelAsync(sourceId);
        await this.setInvoiceIssuedToReceiptAsync(true);

        const companyParameters = this.localContext.getPreloaded(CompanyParametersQuery)!;
        const communityTransactions = this.localContext.getPreloaded(CommunityTransactionForSaleQuery)!;
        const vatRates = this.localContext.getPreloaded(VatRateForSaleQuery)!;

        const numberings = this.localContext.getPreloaded(NumberingQuery)!;
        const loggedUserQueryInfo = this.localContext.getPreloaded(LoggedUserQuery)!;
        const loggedUserId = loggedUserQueryInfo.id;
        const loggedUser = this.localContext.firstPreloaded(UserCollectionQuery, { id: loggedUserId })!;

        const now = Date.now();
        const kind = SaleDocumentKind.Invoice;
        const [{ id: defaultNumberingId }] = numberings.filter(n => n.default && n.kinds.indexOf(kind) > -1);
        const wasWstoOssTransaction = (this.sourceReceiptModel.communityTransactionId === communityTransactions.find(elem => elem.symbol === 'WSTO-OSS')!.id)
            && !companyParameters.ossRegistered;

        // new values
        this.model.kind = kind;
        this.model.numberingId = defaultNumberingId;
        this.model.issueDate = new Date(now);
        this.model.implementedDocumentId = sourceDocumentLink ? sourceDocumentLink.documentId : this.sourceReceiptModel.id;
        this.model.implementedDocumentVersion = sourceDocumentLink ? sourceDocumentLink.version : this.sourceReceiptModel.version;

        this.model.issuedByVatPayer = companyParameters.vatPayer;
        rewriteVatReleaseProperties(this.model, this.sourceReceiptModel, { companyParameters, vatRates });

        this.model.cashBasis = this.sourceReceiptModel.cashBasis;
        this.model.issuer = `${loggedUser.firstName} ${loggedUser.lastName}`;
        this.model.splitPayment = SplitPaymentState.False;
        this.model.jpkVatGroups = this.sourceReceiptModel.jpkVatGroups;
        this.model.invoiceToReceipt = true;
        this.model.bankAccountNumberId = this.sourceReceiptModel.bankAccountNumberId;
        this.model.baseCurrencyBankAccountNumberId = this.sourceReceiptModel.baseCurrencyBankAccountNumberId;

        // rewrite
        this.model.calculationMethod = this.sourceReceiptModel.calculationMethod;
        this.model.communityTransactionId = wasWstoOssTransaction ? communityTransactions.find(elem => elem.default)!.id : this.sourceReceiptModel.communityTransactionId;
        this.model.rateNP = this.sourceReceiptModel.rateNP;
        this.model.reverseCharge = this.sourceReceiptModel.reverseCharge;
        this.model.currencyId = this.sourceReceiptModel.currencyId;
        this.model.saleDate = this.sourceReceiptModel.saleDate;
        if (this.model.issuedByVatPayer) {
            this.model.taxLiabilityDate = this.sourceReceiptModel.taxLiabilityDate || new Date(now);
        }
        this.model.transactionLocation = this.sourceReceiptModel.transactionLocation;

        //#region order must be kept
        this.model.paymentMethodId = this.sourceReceiptModel.paymentMethodId;
        this.model.dueDate = this.sourceReceiptModel.dueDate;
        this.model.dueDateInDays = this.sourceReceiptModel.dueDateInDays;
        //#endregion

        this.model.wstoAdditionalInformation = this.sourceReceiptModel.wstoAdditionalInformation;
        this.model.wstoConsumerCountryId = this.sourceReceiptModel.wstoConsumerCountryId;
        this.model.wstoShipmentCountryId = this.sourceReceiptModel.wstoShipmentCountryId;

        rewriteDocumentContractor(this.model, this.sourceReceiptModel);

        if (this.sourceReceiptModel.deliveryAddress) {
            const deliveryAddress = new AddressModel();
            this.model.deliveryAddress = deliveryAddress;
            rewriteAddress(deliveryAddress, this.sourceReceiptModel.deliveryAddress);
        }

        if (this.sourceReceiptModel.exchangeRate) {
            const exchangeRate = new ExchangeRateModel();
            this.model.exchangeRate = exchangeRate;
            rewriteExchangeRate(exchangeRate, this.sourceReceiptModel.exchangeRate);
        }

        this.sourceReceiptModel.items.map(item => {
            const model = new SaleDocumentItemModel();
            this.model.items.push(model);
            rewriteSaleDocumentItem(model, item);
            model.vatRateId = wasWstoOssTransaction ? vatRates.find(elem => elem.default)!.id : item.vatRateId;
        });

        this.model.inventoryEffect = getCorrectInventoryEffectFromSourceDocument(this.sourceReceiptModel.inventoryEffect);
        this.model.goodsIssueDate = this.model.inventoryEffect ? this.sourceReceiptModel.goodsIssueDate || this.sourceReceiptModel.saleDate : null;

        await this.waitForProcessingCompletionAsync();
    }

    public async fillProFormaOrderAsync(source: SaleProFormaModel): Promise<void> {
        const companyParameters = this.localContext.getPreloaded(CompanyParametersQuery)!;
        const vatRates = this.localContext.getPreloaded(VatRateForSaleQuery)!;
        const communityTransactions = this.localContext.getPreloaded(CommunityTransactionForSaleQuery)!;
        const documentConfiguration = this.localContext.firstPreloaded(SaleKindQuery, { name: this.model.kind })!;
        const proFormaOrderCalculationMethod = this.model.kind === SaleDocumentKind.Receipt ? documentConfiguration.defaultCalculationMethod : null as any;
        const numberings = this.localContext.getPreloaded(NumberingQuery)!;

        await rewriteSaleDocumentFromProFormaAsync(this.model, source, { companyParameters, communityTransactions, defaultCalculationMethod: proFormaOrderCalculationMethod, vatRates }, this.clientRepository);

        this.model.payments.forEach(p => p.onDocument = true);

        const reverseChargeCommunityTransaction = this.localContext.firstPreloaded(CommunityTransactionForSaleQuery, { reverseCharge: true })!;
        if (this.model.kind === SaleDocumentKind.Receipt &&
            source.communityTransactionId === reverseChargeCommunityTransaction.id) {
            this.setComunityTransactionAsync(this.model.communityTransactionId);
        }
        if (documentConfiguration.useCustomTransactionLocation) {
            this.model.transactionLocation = documentConfiguration.customTransactionLocation;
        } else {
            const { city } = this.localContext.getPreloaded<Company>(ContextKeys.company)!;
            this.model.transactionLocation = city;
        }

        if (source.exchangeRate) {
            await this.refreshExchangeRateAsync();
        }

        const [{ id: defaultNumberingId }] = numberings.filter(n => n.default && n.kinds.indexOf(this.model.kind) > -1);
        this.model.numberingId = defaultNumberingId;

        if (this.model.kind === SaleDocumentKind.Receipt) {
            this.model.splitPayment = SplitPaymentState.False;
            this.model.jpkVatGroups = this.model.jpkVatGroups.filter(code => code !== JpkVatCodes.MPP);
        } else {
            this.model.splitPayment = source.splitPayment;
        }

        this.model.implementedDocumentId = source.id;
        this.model.implementedDocumentVersion = source.version;
        
        const transaction = communityTransactions.find(elem => elem.id === source.communityTransactionId)!;
        const defaults = getDefaultNPValues(transaction.symbol);
        this.model.reverseCharge = defaults.reverseCharge;
        this.model.rateNP = defaults.rateNP;

        await this.waitForProcessingCompletionAsync();
    }

    private internalInvoiceIssuedToInventoryGoodsIssue = false;
    private async setInvoiceIssuedToInventoryGoodsIssueAsync(value: boolean) {
        this.internalInvoiceIssuedToInventoryGoodsIssue = value;
        await this.localContext.refresh(ContextKeys.isInvoiceToInventoryGoodsIssue(this.model.sun));
    }

    public get invoiceIssuedToInventoryGoodsIssue(): boolean {
        return this.internalInvoiceIssuedToInventoryGoodsIssue;
    }

    private internalRelatedGoodsIssueDocument!: InventoryGoodsIssueDocumentModel;
    private async setRelatedGoodsIssueDocument(value: InventoryGoodsIssueDocumentModel) {
        this.internalRelatedGoodsIssueDocument = value;
        await this.localContext.refresh(ContextKeys.relatedGoodsIssueDocumentToInvoice(this.model.sun));
    }

    public async fillInvoiceToGoodsIssueAsync(source: InventoryGoodsIssueDocumentModel | InventoryGoodsIssueAdjustmentDocumentModel, relatedDocuments: RelatedEntity[], kind: SaleDocumentKind): Promise<void> {
        await this.setInvoiceIssuedToInventoryGoodsIssueAsync(true);
        await this.setRelatedGoodsIssueDocument(source);

        const documentConfig = this.localContext.firstPreloaded(SaleKindQuery, { name: kind })!;
        const vatRates = this.localContext.getPreloaded(VatRateForSaleQuery)!;
        const companyParameters = this.localContext.getPreloaded(CompanyParametersQuery)!;
        const communityTransactions = this.localContext.getPreloaded(CommunityTransactionForSaleQuery)!;
        const defaultCommunityTransaction = this.localContext.firstPreloaded(CommunityTransactionForSaleQuery, { default: true })!;
        const numberings = this.localContext.getPreloaded(NumberingQuery)!;

        const wasWstoTransaction = (source.communityTransactionId === communityTransactions.find(elem => elem.symbol === 'WSTO-OSS')!.id)
            && !companyParameters.ossRegistered;
        const communityTransactionUnavailableForReceipt = (kind === SaleDocumentKind.Receipt) && !CommunityTransactionsForReceipt.includes(communityTransactions.find(elem => elem.id === source.communityTransactionId)!.symbol);

        this.model.relatedInventoryDocuments = [...relatedDocuments];
        this.model.inventoryEffect = InventoryEffect.Realized;
        this.model.goodsIssueDate = source.goodsIssueDate;

        this.model.kind = kind;
        this.model.issuedByVatPayer = companyParameters.vatPayer;

        if(!this.model.issuedByVatPayer) {
            this.model.vatReleaseReasonId = companyParameters.vatReleaseReasonId;
            this.model.vatReleaseReasonSource = companyParameters.vatReleaseReasonSource;
            this.model.vatReleaseReasonCustomName = companyParameters.vatReleaseReasonCustomName;
        }

        await rewriteDocumentContractorWithFreshDataAsync(this.model, source, this.clientRepository);

        this.model.calculationMethod = !this.model.issuedByVatPayer || this.model.kind === SaleDocumentKind.Receipt ? CalculationMethod.Gross : source.calculationMethod;
        this.model.communityTransactionId = !this.model.issuedByVatPayer || wasWstoTransaction || communityTransactionUnavailableForReceipt ? defaultCommunityTransaction.id : source.communityTransactionId;
        this.model.currencyId = source.currencyId;

        const [{ id: defaultNumberingId }] = numberings.filter(n => n.default && n.kinds.includes(this.model.kind));
        this.model.numberingId = defaultNumberingId;

        this.model.saleDate = source.goodsIssueDate;
        this.model.issueDate = new Date();
        if (this.model.issuedByVatPayer) this.model.taxLiabilityDate = new Date();

        this.model.transactionLocation = source.transactionLocation;
        this.model.recipient = source.recipient;
        this.model.issuer = source.issuer;

        this.model.splitPayment = SplitPaymentState.False;

        this.model.paymentMethodId = documentConfig.defaultPaymentMethodId;
        this.model.dueDateInDays = documentConfig.defaultDueDate;

        this.model.wstoAdditionalInformation = source.wstoAdditionalInformation;
        this.model.wstoConsumerCountryId = source.wstoConsumerCountryId;
        this.model.wstoShipmentCountryId = source.wstoShipmentCountryId;

        source.items.forEach(item => {
            const model = new SaleDocumentItemModel();
            this.model.items.push(model);
            rewriteDocumentItem(model, item);
            model.vatRateId = this.model.issuedByVatPayer
                ? wasWstoTransaction ? vatRates.find(elem => elem.default)!.id : item.vatRateId
                : vatRates.find(elem => elem.exempt)!.id;
            model.relatedInventoryItemsIds = [item.id!];
            model.includesInventoryControl = true;
        });

        this.model.cashBasis = companyParameters.cashBasis;
        this.model.jpkVatGroups = unique(getJpkVatGroups(this.model.items));

        if (source.exchangeRate) {
            const exchangeRate = new ExchangeRateModel();
            this.model.exchangeRate = exchangeRate;
            rewriteExchangeRate(exchangeRate, source.exchangeRate);
        }

        if (source.deliveryAddress) {
            const deliveryAddress = new AddressModel();
            this.model.deliveryAddress = deliveryAddress;
            rewriteAddress(deliveryAddress, source.deliveryAddress);
        }
        
        const transaction = communityTransactions.find(elem => elem.id === source.communityTransactionId)!;
        const defaults = getDefaultNPValues(transaction.symbol);
        this.model.reverseCharge = defaults.reverseCharge;
        this.model.rateNP = defaults.rateNP;

        if (this.model.issuedByVatPayer) this.recalculateTaxLiabilityDate();
        await this.updateBankAccountNumber();
    }

    public async fillInvoiceToOrder(source: OrderModel, kind: SaleDocumentKind): Promise<void> {
        const documentConfig = this.localContext.firstPreloaded(SaleKindQuery, { name: kind })!;
        const vatRates = this.localContext.getPreloaded(VatRateForSaleQuery)!;
        const units = this.localContext.getPreloaded(UnitQuery)!;
        const companyParameters = this.localContext.getPreloaded(CompanyParametersQuery)!;
        const currencies = this.localContext.getPreloaded(CurrencyCollectionQuery)!;
        const countries = this.localContext.getPreloaded(CountryCollectionQuery)!;
        const defaultCommunityTransaction = this.localContext.firstPreloaded(CommunityTransactionForSaleQuery, { default: true })!;
        const numberings = this.localContext.getPreloaded(NumberingQuery)!;
        const { city } = this.localContext.getPreloaded<Company>(ContextKeys.company)!;
        const loggedUserQueryInfo = this.localContext.getPreloaded(LoggedUserQuery)!;
        const loggedUserId = loggedUserQueryInfo.id;
        const loggedUser = this.localContext.firstPreloaded(UserCollectionQuery, { id: loggedUserId })!;

        this.model.kind = kind;
        this.model.issuedByVatPayer = companyParameters.vatPayer;
        this.model.orderExternalId = source.id;

        if(!this.model.issuedByVatPayer) {
            this.model.vatReleaseReasonId = companyParameters.vatReleaseReasonId;
            this.model.vatReleaseReasonSource = companyParameters.vatReleaseReasonSource;
            this.model.vatReleaseReasonCustomName = companyParameters.vatReleaseReasonCustomName;
        }

        this.model.client = new DocumentClientModel();
        fillDocumentClientFromOrderClient(this.model.client, source.recipient, countries);

        this.model.calculationMethod = CalculationMethod.Gross;
        this.model.communityTransactionId = defaultCommunityTransaction.id;
        this.model.currencyId = currencies.find(e => e.externalId === source.currencyId)!.id;

        const [{ id: defaultNumberingId }] = numberings.filter(n => n.default && n.kinds.includes(this.model.kind));
        this.model.numberingId = defaultNumberingId;

        this.model.saleDate = new Date();
        this.model.issueDate = new Date();
        if (this.model.issuedByVatPayer) this.model.taxLiabilityDate = new Date();

        this.model.transactionLocation = documentConfig.useCustomTransactionLocation ? documentConfig.customTransactionLocation : city;
        this.model.issuer = `${loggedUser.firstName} ${loggedUser.lastName}`;

        this.model.splitPayment = SplitPaymentState.False;

        this.model.paymentMethodId = documentConfig.defaultPaymentMethodId;
        this.model.dueDateInDays = documentConfig.defaultDueDate;

        let itemsIdToExternalIdMap: ProductGetIdsByExternalIdsResult = [];
        if (source.items.length && source.items.some(e => !!e.productId)) {
            const externalIds = source.items.map(e => e.productId).filter(Boolean) as string[];
            itemsIdToExternalIdMap = await this.queryBus.queryAsync(new ProductGetIdsByExternalIdsQuery({externalIds}));
        }

        source.items.forEach(product => {
            const model = new SaleDocumentItemModel();
            const productId = itemsIdToExternalIdMap.find(e => e.externalId === product.productId)?.id || null;

            this.model.items.push(model);
            model.productId = productId || null;
            model.productName = product.name;
            model.symbol = product.symbol;
            model.discountKind = DiscountKind.Percentage,
            model.includesInventoryControl = productId ? !!product.inventoryControl : false;
            model.vatRateId = this.localContext.firstPreloaded(VatRateForSaleQuery, {externalId: product.vatRateId})!.id;
            model.unitId = this.localContext.firstPreloaded(UnitQuery, {externalId: product.unitExternalId})!.id;
            model.price_grossAfterDiscount = product.price_grossAfterDiscount;
            model.price_grossBeforeDiscount = product.price_grossBeforeDiscount;
            model.quantity = product.quantity;
        })

        if (source.delivery && source.delivery.grossCost > 0) {
            const deliveryCost = new SaleDocumentItemModel();
            this.model.items.push(deliveryCost);
            deliveryCost.productName = source.delivery.shipmentMethod?.serviceName || 'Dostawa';
            deliveryCost.vatRateId = vatRates!.find(e => e.externalId === source.delivery?.taxExternalId)!.id;
            deliveryCost.price_netBeforeDiscount = source.delivery.netCost;
            deliveryCost.discountKind = DiscountKind.Percentage,
            deliveryCost.price_grossBeforeDiscount = source.delivery.grossCost;
            deliveryCost.unitId = units!.find(e => e.default)!.id;
            deliveryCost.quantity = 1;
        }

        this.model.cashBasis = companyParameters!.cashBasis;

        rewritePaymentMethodFromOrderDocument(source, this.model, this.localContext.getPreloaded(PaymentMethodQuery)!);

        if (source.payments.length) {
            const payment = new DocumentPaymentListItemModel();
            this.model.payments.push(payment);
            payment.date = getMaxOfDates(...source.payments?.map(e => e.finishedAt) || []) || new Date();
            payment.value = source.paymentSummary.paidAmount > source.paymentSummary.totalAmount ? source.paymentSummary.totalAmount : source.paymentSummary.paidAmount;
            if (!source.paymentSummary.pendingAmount) {
                this.model.dueDateInDays = 0;
            }
        }

        if (this.model.issuedByVatPayer) this.recalculateTaxLiabilityDate();
        this.updateBankAccountNumber();
    }

    public async refreshDocumentItemsPricesAsync(): Promise<void> {
        await Promise.all(this.model.items.map(async item => await refreshSaleDocumentItemPriceAsync(item, this, this.productRepository)));
    }
}
